import React from 'react';
import { difference, omitBy } from 'lodash';

export type SelectionState = {
    systemSelectedItemIds: Record<number, number>;
    selectedItemIds: Record<number, number>;
    itemsToggledByUser: Record<number, true>;
};

type SelectionAction =
    | {
          type: 'TOGGLE_SELECTION';
          payload: number;
      }
    | {
          type: 'RESET_SYSTEM_SELECTED_ITEMS';
          payload: number[];
      };

export function selectionStateReducer(
    state: SelectionState,
    action: SelectionAction
): SelectionState {
    switch (action.type) {
        case 'RESET_SYSTEM_SELECTED_ITEMS': {
            const newSystemSelectedItems = Object.fromEntries(
                // we filter out items that have ever been interacted with by the user, because we take
                // the users explicit actions to have priority over updates coming from the system
                // (so, if a user has selected an item, we want it to stay selected regardless of system state,
                // and if a user has de-selected an item, we want it to stay de-selected regardless of system state)
                action.payload.filter((id) => !state.itemsToggledByUser[id]).map((id) => [id, id])
            );
            const newCurrentSelectedItems = omitBy(
                state.selectedItemIds,
                // we remove selected items which aren't in the new system selected items and haven't been toggled by the user
                (id) => !state.itemsToggledByUser[id] && !newSystemSelectedItems[id]
            );
            return {
                ...state,
                systemSelectedItemIds: newSystemSelectedItems,
                selectedItemIds: {
                    ...newSystemSelectedItems,
                    ...newCurrentSelectedItems,
                },
            };
        }
        case 'TOGGLE_SELECTION': {
            return {
                ...state,
                itemsToggledByUser: {
                    ...state.itemsToggledByUser,
                    ...{ [action.payload]: true },
                },
                selectedItemIds: state.selectedItemIds[action.payload]
                    ? omitBy(state.selectedItemIds, (id) => id === action.payload)
                    : {
                          ...state.selectedItemIds,
                          [action.payload]: action.payload,
                      },
            };
        }
    }
}

type UseSelectionStateReturns = {
    selectedItemIds: number[];
    unselectedItemIds: number[];
    toggleItemSelection: (itemId: number) => void;
    resetInitiallySelectedItems: (itemIds: number[]) => void;
};

export function useSelectionState(): UseSelectionStateReturns {
    const [state, dispatch] = React.useReducer(selectionStateReducer, {
        systemSelectedItemIds: {},
        selectedItemIds: {},
        itemsToggledByUser: {},
    });
    const systemSelectedItemIds = React.useMemo(() => {
        return Object.values(state.systemSelectedItemIds);
    }, [state]);
    const selectedItemIds = React.useMemo(() => {
        return Object.values(state.selectedItemIds);
    }, [state.selectedItemIds]);
    const unselectedItemIds = React.useMemo(() => {
        return difference(systemSelectedItemIds, selectedItemIds);
    }, [selectedItemIds, systemSelectedItemIds]);
    const toggleItemSelection = React.useCallback(
        (id: number) => {
            dispatch({
                type: 'TOGGLE_SELECTION',
                payload: id,
            });
        },
        [dispatch]
    );
    const resetInitiallySelectedItems = React.useCallback(
        (items: number[]) => {
            dispatch({
                type: 'RESET_SYSTEM_SELECTED_ITEMS',
                payload: items,
            });
        },
        [dispatch]
    );
    return {
        selectedItemIds,
        unselectedItemIds,
        toggleItemSelection,
        resetInitiallySelectedItems,
    };
}
